import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { PLANS, createPaymentPreference, loadMercadoPagoSDK } from "../services/mercadopago";
import { C, Btn, Card, SectionTitle } from "../components/UI";

const CHECK = "✓";
const CROSS = "✗";

function PlanCard({ plan, onSelect, loading, isSelected }) {
  return (
    <div style={{ position: "relative" }}>
      {plan.popular && (
        <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: C.roseMid, color: "#fff", fontSize: 11, fontWeight: 500, padding: "4px 16px", borderRadius: 100, whiteSpace: "nowrap", zIndex: 1 }}>
          Mais escolhido
        </div>
      )}
      <Card style={{ border: plan.popular ? `2px solid ${C.roseMid}` : `0.5px solid ${C.border}`, padding: "28px 24px", height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500, color: C.textSec, textTransform: "uppercase", letterSpacing: "1px" }}>{plan.name}</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          {plan.price ? `R$ ${plan.price}` : plan.priceLabel}
          {plan.period && <span style={{ fontSize: 14, fontWeight: 400, color: C.textSec, fontFamily: "'DM Sans', sans-serif" }}>{plan.period}</span>}
        </div>
        <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6, marginBottom: 20 }}>{plan.description}</p>

        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, marginBottom: 28, flex: 1 }}>
          {plan.features.map(f => (
            <li key={f} style={{ fontSize: 13, color: C.text, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>{CHECK}</span>{f}
            </li>
          ))}
          {plan.notIncluded.map(f => (
            <li key={f} style={{ fontSize: 13, color: C.gray, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ color: C.gray, flexShrink: 0 }}>{CROSS}</span>{f}
            </li>
          ))}
        </ul>

        <Btn
          full
          variant={plan.popular ? "primary" : "secondary"}
          onClick={() => onSelect(plan)}
          disabled={loading && isSelected}
        >
          {loading && isSelected ? "Aguarde..." : plan.price ? "Assinar agora" : "Falar com vendas"}
        </Btn>
      </Card>
    </div>
  );
}

// ── Checkout modal ─────────────────────────────────────────────────────────────
function CheckoutModal({ plan, onClose, company }) {
  const [step, setStep] = useState("confirm"); // confirm | processing | success | error
  const [preferenceId, setPreferenceId] = useState(null);
  const [mpReady, setMpReady] = useState(false);

  useEffect(() => {
    loadMercadoPagoSDK().then(() => setMpReady(true));
  }, []);

  const startPayment = async () => {
    setStep("processing");
    try {
      // Em produção, isso chama seu backend que retorna o preference_id do Mercado Pago
      // const data = await createPaymentPreference(plan, company);
      // setPreferenceId(data.preference_id);

      // SIMULAÇÃO (remova em produção):
      await new Promise(r => setTimeout(r, 1500));
      setStep("mp_checkout");
    } catch (e) {
      setStep("error");
    }
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeIn 0.2s ease" };
  const modal = { background: "#fff", borderRadius: 20, padding: "36px 32px", width: "100%", maxWidth: 460, animation: "fadeUp 0.3s ease" };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {step === "confirm" && (
          <>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, marginBottom: 6 }}>Confirmar assinatura</h2>
            <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24 }}>Você está assinando o plano <strong>{plan.name}</strong></p>

            <div style={{ background: C.rosePale, border: `0.5px solid ${C.roseBorder}`, borderRadius: 12, padding: "16px 20px", marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: C.textSec }}>Plano {plan.name}</span>
                <span style={{ fontSize: 16, fontWeight: 500 }}>R$ {plan.price}/mês</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: C.textSec }}>Recorrência</span>
                <span style={{ fontSize: 13, color: C.textSec }}>Mensal · cancele quando quiser</span>
              </div>
            </div>

            <div style={{ background: "#EAF3DE", border: "0.5px solid #C0DD97", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: C.green, marginBottom: 24, display: "flex", gap: 8, alignItems: "center" }}>
              🔒 Pagamento 100% seguro via Mercado Pago
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="secondary" full onClick={onClose}>Cancelar</Btn>
              <Btn variant="primary" full onClick={startPayment}>Ir para pagamento →</Btn>
            </div>
          </>
        )}

        {step === "processing" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 8 }}>Preparando pagamento...</h2>
            <p style={{ fontSize: 14, color: C.textSec }}>Conectando com o Mercado Pago</p>
          </div>
        )}

        {step === "mp_checkout" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>💳</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 8 }}>Checkout Mercado Pago</h2>
            <p style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>
              Em produção, o Checkout Pro do Mercado Pago abre aqui automaticamente com o <code>preference_id</code> gerado pelo seu backend.
            </p>
            <div style={{ background: C.grayBg, borderRadius: 12, padding: "16px", marginBottom: 20, textAlign: "left", fontSize: 12, color: C.textSec, lineHeight: 1.8 }}>
              <strong>Como funciona em produção:</strong><br />
              1. Backend cria preferência via API do MP<br />
              2. Retorna o <code>init_point</code> (URL) ou <code>preference_id</code><br />
              3. Frontend redireciona ou abre o Checkout Pro<br />
              4. MP confirma via webhook no backend<br />
              5. Backend ativa o plano da empresa
            </div>
            {/* Em produção, este botão abre o Checkout Pro: */}
            {/* <div id="mp-checkout-btn"></div> */}
            <Btn full onClick={() => setStep("success")}>Simular pagamento aprovado ✓</Btn>
            <Btn variant="ghost" full onClick={onClose} style={{ marginTop: 8, fontSize: 13 }}>Fechar</Btn>
          </div>
        )}

        {step === "success" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.greenBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 20px" }}>✓</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: C.green, marginBottom: 8 }}>Pagamento aprovado!</h2>
            <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24 }}>Seu plano <strong>{plan.name}</strong> está ativo. Bem-vinda ao painel!</p>
            <Btn full onClick={onClose}>Ir para o painel →</Btn>
          </div>
        )}

        {step === "error" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 8, color: C.red }}>Erro no pagamento</h2>
            <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24 }}>Não foi possível conectar com o Mercado Pago. Tente novamente.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="secondary" full onClick={onClose}>Fechar</Btn>
              <Btn full onClick={startPayment}>Tentar novamente</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Plans Page ─────────────────────────────────────────────────────────────────
export default function Plans() {
  const { user, userType, setCompanyPlan } = useApp();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSelect = (plan) => {
    if (!user) { navigate("/cadastro-empresa"); return; }
    if (userType !== "company") { navigate("/cadastro-empresa"); return; }
    if (plan.id === "enterprise") { navigate("/contato"); return; }
    setSelectedPlan(plan);
  };

  const handleCloseModal = () => {
    if (selectedPlan) setCompanyPlan(selectedPlan.id);
    setSelectedPlan(null);
    if (selectedPlan) navigate("/dashboard");
  };

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 60%)`, padding: "60px 80px 48px", textAlign: "center" }}>
        <SectionTitle
          tag="Planos para empresas"
          title="Construa sua reputação com quem importa"
          sub="Mostre que sua empresa é um lugar onde mulheres querem trabalhar e crescer."
          center
        />
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: 100, padding: "8px 18px", fontSize: 13, color: C.green }}>
          ✓ Para mulheres, acesso gratuito para sempre
        </div>
      </div>

      <div style={{ padding: "48px 80px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, alignItems: "start", paddingTop: 16 }}>
          {Object.values(PLANS).map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onSelect={handleSelect}
              loading={loadingPlan === plan.id}
              isSelected={loadingPlan === plan.id}
            />
          ))}
        </div>

        {/* Garantias */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20, marginTop: 48, paddingTop: 40, borderTop: `0.5px solid ${C.border}` }}>
          {[
            { icon: "🔒", title: "Pagamento seguro", desc: "Mercado Pago com criptografia SSL. Seus dados financeiros nunca passam pelos nossos servidores." },
            { icon: "🔄", title: "Cancele quando quiser", desc: "Sem multa, sem burocracia. Cancele a assinatura a qualquer momento pelo painel." },
            { icon: "📞", title: "Suporte dedicado", desc: "Nossa equipe está disponível para ajudar sua empresa a tirar o máximo do WoMom." },
          ].map(g => (
            <div key={g.title} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{g.icon}</div>
              <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{g.title}</h3>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>{g.desc}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div style={{ marginTop: 56 }}>
          <SectionTitle tag="Dúvidas" title="Perguntas frequentes" center />
          <div style={{ maxWidth: 640, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { q: "Como funciona o pagamento recorrente?", a: "A cobrança é feita mensalmente via Mercado Pago na data de contratação. Você recebe um e-mail de confirmação a cada renovação." },
              { q: "Posso mudar de plano depois?", a: "Sim! Você pode fazer upgrade ou downgrade a qualquer momento pelo painel. O valor é ajustado proporcionalmente." },
              { q: "Há contrato de fidelidade?", a: "Não. Nossos planos são mensais sem fidelidade. Cancele quando quiser, sem multa." },
              { q: "Como é feita a verificação da empresa?", a: "Após a contratação, nossa equipe valida o CNPJ e e-mail corporativo em até 24h úteis." },
            ].map(f => (
              <Card key={f.q} style={{ padding: "16px 20px" }}>
                <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{f.q}</h4>
                <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>{f.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {selectedPlan && (
        <CheckoutModal
          plan={selectedPlan}
          company={user}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
